import { type TFunction } from 'i18next'
import * as R from 'remeda'

import { compact } from '../generals/compact'

import {
  type FiveNumber,
  type FiveSegment,
  type FiveSegmentCounts,
  type FiveSegmentIndicator,
  fiveSegments,
  fiveToNineSegmentsMap,
  type NineNumber,
  nineNumbers,
  type NineSegment,
  nineSegmentAttributeI18nKeyMap,
  type NineSegmentCounts,
  type NineSegmentIndicator,
  nineSegmentIntentionI18nKeyMap,
  nineSegments,
} from './segments'

/**
 * NineNumber を NineSegment に変換します。
 *
 * @param nineNumber - NineNumber。
 * @returns NineSegment。
 */
export const toNineSegment = (nineNumber: NineNumber): NineSegment =>
  `nineSegment${nineNumber}`

/**
 * NineSegment を NineNumber に変換します。
 *
 * @param nineSegment - NineSegment。
 * @returns NineNumber
 */
export const toNineNumber = (nineSegment: NineSegment): NineNumber =>
  Number(nineSegment.replace('nineSegment', '')) as NineNumber

/**
 * NineNumber をキーとするオブジェクトを返します。
 *
 * @param mapFunc - 値を生成する関数。
 * @returns NineNumber をキーとするオブジェクト。
 */
export const mapNineNumber = <T>(
  mapFunc: (nineNumber: NineNumber, index: number) => T,
): Record<NineNumber, T> =>
  R.mapToObj(nineNumbers, (nineNumber, index) => [
    nineNumber,
    mapFunc(nineNumber, index),
  ])

/**
 * NineSegment をキーとするオブジェクトを返します。
 *
 * @param mapFunc - 値を生成する関数。
 * @returns NineSegment をキーとするオブジェクト。
 */
export const mapNineSegment = <T>(
  mapFunc: (nineSegment: NineSegment, index: number) => T,
): Record<NineSegment, T> =>
  R.mapToObj(nineSegments, (nineSegment, index) => [
    nineSegment,
    mapFunc(nineSegment, index),
  ])

/**
 * NineSegment のカウントを合計します。
 *
 * @param nineSegmentCounts - NineSegment のカウント。
 * @param nineNumbers - 合計する NineNumber の配列。
 * @returns 合計値。
 */
export const sumNineNumbers = (
  nineSegmentCounts: NineSegmentCounts,
  ...nineNumbers: NineNumber[]
) =>
  R.sumBy(
    nineNumbers,
    (nineNumber) => nineSegmentCounts[toNineSegment(nineNumber)],
  )

/**
 * NineSegment のカウントの差分を計算します。
 *
 * @param nineSegmentCounts - NineSegment のカウント。
 * @param minuendNineNumber - 引かれる数の NineNumber。
 * @param subtrahendNineNumber - 引く数の NineNumber。
 * @returns 差分。
 */
export const diffNineNumbers = (
  nineSegmentCounts: NineSegmentCounts,
  minuendNineNumber: NineNumber,
  subtrahendNineNumber: NineNumber,
) =>
  nineSegmentCounts[toNineSegment(minuendNineNumber)] -
  nineSegmentCounts[toNineSegment(subtrahendNineNumber)]

/**
 * FiveNumber を FiveSegment に変換します。
 *
 * @param fiveNumber - FiveNumber。
 * @returns FiveSegment。
 */
export const toFiveSegment = (fiveNumber: FiveNumber): FiveSegment =>
  `fiveSegment${fiveNumber}`

/**
 * FiveSegment をキーとするオブジェクトを返します。
 *
 * @param mapFunc - 値を生成する関数。
 * @returns FiveSegment をキーとするオブジェクト。
 */
export const mapFiveSegment = <T>(
  mapFunc: (fiveSegment: FiveSegment, index: number) => T,
): Record<FiveSegment, T> =>
  R.mapToObj(fiveSegments, (fiveSegment, index) => [
    fiveSegment,
    mapFunc(fiveSegment, index),
  ])

/**
 * FiveSegment のカウントを合計します。
 *
 * @param fiveSegmentCounts - FiveSegment のカウント。
 * @param fiveNumbers - 合計する FiveNumber の配列。
 * @returns 合計値。
 */
export const sumFiveNumbers = (
  fiveSegmentCounts: FiveSegmentCounts,
  ...fiveNumbers: FiveNumber[]
) =>
  R.sumBy(
    fiveNumbers,
    (fiveNumber) => fiveSegmentCounts[toFiveSegment(fiveNumber)],
  )

/**
 * FiveSegment のカウントの差分を計算します。。
 *
 * @param fiveSegmentCounts - FiveSegment のカウント。
 * @param minuendFiveNumber - 引かれる数の FiveNumber。
 * @param subtrahendFiveNumber - 引く数の FiveNumber。
 * @returns 差分。
 */
export const diffFiveNumbers = (
  fiveSegmentCounts: FiveSegmentCounts,
  minuendFiveNumber: FiveNumber,
  subtrahendFiveNumber: FiveNumber,
) =>
  fiveSegmentCounts[toFiveSegment(minuendFiveNumber)] -
  fiveSegmentCounts[toFiveSegment(subtrahendFiveNumber)]

/**
 * NineSegment のカウントから FiveSegment のカウントを生成します。
 *
 * @param nineSegmentCounts - NineSegment のカウント。
 * @returns FiveSegment のカウント。
 */
export const toFiveSegmentCounts = (
  nineSegmentCounts: NineSegmentCounts,
): FiveSegmentCounts =>
  mapFiveSegment((fiveSegment) =>
    sumNineNumbers(
      nineSegmentCounts,
      ...fiveToNineSegmentsMap[fiveSegment].map(toNineNumber),
    ),
  )

/**
 * NineSegment / NineNumber を指定された書式に従ってテキストにフォーマットします。
 *
 * 書式は、NineSegment / NineNumber を表す文字列に、以下の文字列を含めることができます。
 *
 * - `%n` - NineNumber を表す文字列に置き換えられます。
 * - `%i` - nineSegmentIntention を表す文字列に置き換えられます。
 * - `%a` - nineSegmentAttribute を表す文字列に置き換えられます。
 * - `%l` - nineSegmentIntention と nineSegmentAttribute を改行して結合した文字列に置き換えられます。
 * - `%t` - nineSegmentIntention と nineSegmentAttribute を結合した文字列に置き換えられます。
 *          区切り文字はロケール依存（日本語: なし、英語: 半角スペース）です。
 *          `%t2` のように数字を指定することで、指定された数の半角スペースで区切られた文字列に置き換えられます。
 *
 * @param nineSegmentOrNumber - NineSegment または NineNumber。
 * @param format - 書式。
 * @param t - i18next の翻訳関数。
 * @returns テキスト
 *
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export const formatNineSegment = (
  nineSegmentOrNumber: NineNumber | NineSegment,
  format: string,
  t: TFunction,
) => {
  const [nineSegment, nineNumber] =
    typeof nineSegmentOrNumber === 'number'
      ? [toNineSegment(nineSegmentOrNumber), nineSegmentOrNumber]
      : [nineSegmentOrNumber, toNineNumber(nineSegmentOrNumber)]

  const intention = t(nineSegmentIntentionI18nKeyMap[nineSegment])
  const attribute = t(nineSegmentAttributeI18nKeyMap[nineSegment])

  return format
    .replace('%n', `${nineNumber}`)
    .replace('%i', intention ?? '')
    .replace('%a', attribute)
    .replace('%l', compact([intention, attribute]).join('\n'))
    .replace(/%t(\d*)/g, (_, spaceCountString: string) => {
      // 数字指定がない `%t` はロケール依存のセパレータで結合する。
      // （日本語は空文字、英語は半角スペース。「PassiveAware」のような連結を防ぐ）
      // `%t1` のように数字を指定した場合は、その数の半角スペースで結合する。
      const separator =
        spaceCountString === ''
          ? t('nine_segment_intention_attribute_separator')
          : ' '.repeat(Number(spaceCountString))

      return compact([intention, attribute]).join(separator)
    })
}

/**
 * NineSegmentのパーセンテージと指標を計算します。
 *
 * @param nineSegmentCounts - NineSegment のカウント。
 * @param sampleSize - サンプルサイズ。
 * @returns NineSegment のパーセンテージと指標。
 */
export const calcNineSegmentsRatioAndIndicators = (
  nineSegmentCounts: NineSegmentCounts,
  sampleSize: number,
): NineSegmentCounts & Record<NineSegmentIndicator, number> => ({
  ...R.mapValues(nineSegmentCounts, (value) => value / sampleSize),
  npi: sumNineNumbers(nineSegmentCounts, 7, 5, 3, 1) / sampleSize,
  aNpi:
    sumNineNumbers(nineSegmentCounts, 7, 5, 3, 1) /
    sumNineNumbers(nineSegmentCounts, 8, 7, 6, 5, 4, 3, 2, 1),
  nNpi: sumNineNumbers(nineSegmentCounts, 7, 5) / sampleSize,
  uNpi:
    sumNineNumbers(nineSegmentCounts, 3, 1) /
    sumNineNumbers(nineSegmentCounts, 4, 3, 2, 1),
})

/**
 * FiveSegmentのパーセンテージと指標を計算します。
 *
 * @param fiveSegmentCounts - FiveSegment のカウント。
 * @param sampleSize - サンプルサイズ。
 * @returns FiveSegment のパーセンテージと指標。
 */
export const calcFiveSegmentsRatioAndIndicators = (
  fiveSegmentCounts: FiveSegmentCounts,
  sampleSize: number,
): FiveSegmentCounts & Record<FiveSegmentIndicator, number> => {
  const recognition = sumFiveNumbers(fiveSegmentCounts, 4, 3, 2, 1)
  const currentUser = sumFiveNumbers(fiveSegmentCounts, 2, 1)
  const loyal = fiveSegmentCounts.fiveSegment1
  const user = sumFiveNumbers(fiveSegmentCounts, 3, 2, 1)

  return {
    ...R.mapValues(fiveSegmentCounts, (value) => value / sampleSize),
    rateRecognition: recognition / sampleSize,
    rateCurrentUser: currentUser / sampleSize,
    rateCv: user / recognition,
    rateLoyal: loyal / sampleSize,
    ratioLoyalToCurrentUser: loyal / currentUser,
  }
}
